import { IncentiveCategory } from '@varo/graphql-schema-types';
import { useFocusEffect } from 'expo-router';
import {
  IncentiveErrorStep,
  IncentiveScreenNames,
} from 'features/incentive/helpers/incentiveAnalytics';
import {
  useIncentiveAnalytics,
  useIncentiveScreenViewed,
} from 'features/incentive/hooks/useIncentiveAnalytics';
import { useTranslation } from 'react-i18next';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useEvent from 'shared/hooks/useEvent/useEvent';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';

interface IncentiveErrorScreenProps {
  incentiveType: IncentiveCategory;
  errorStep: IncentiveErrorStep;
  errorCode?: string;
  title?: string;
  message?: string;
  isRetrying: boolean;
  onRetry: () => void;
}

function IncentiveErrorScreen({
  incentiveType,
  errorStep,
  errorCode,
  title,
  message,
  isRetrying,
  onRetry,
}: IncentiveErrorScreenProps) {
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: 'incentiveScreen',
  });
  const { defaultTitle, defaultMessage, retryButtonTitle } =
    useDefaultErrorStrings();
  const { logErrorEncountered } = useIncentiveAnalytics({ incentiveType });

  useIncentiveScreenViewed({
    screenName: IncentiveScreenNames.ERROR,
    incentiveType,
  });

  const handleFocus = useEvent(() => {
    logErrorEncountered({ errorStep, errorCode });
  });

  useFocusEffect(handleFocus);

  return (
    <ErrorScreen
      screenType={ErrorScreenType.Error}
      messageHeadline={title ?? defaultTitle}
      messageBody={message ?? defaultMessage}
      primaryButton={{
        title: retryButtonTitle,
        onPress: onRetry,
        isLoading: isRetrying,
      }}
      secondaryButton={{
        title: translatedText('goBack'),
        onPress: navigateBack,
      }}
    />
  );
}

export default IncentiveErrorScreen;
